/**
 * Sistema de Gamificación Educativa de GeoCeuta
 * Niveles, Puntos de Experiencia (XP) y Rangos de Geómetra para Primaria
 */

export const RANKS = [
    {
        level: 1,
        title: "Explorador de Puntos",
        icon: "📍",
        badgeColor: "bg-blue-500",
        gradient: "from-blue-500 to-cyan-500",
        bgLight: "bg-blue-50",
        textColor: "text-blue-600",
        minXp: 0,
        maxXp: 149,
        description: "Estás comenzando tu viaje por el fascinante universo de la geometría."
    },
    {
        level: 2,
        title: "Maestro de Líneas",
        icon: "📏",
        badgeColor: "bg-emerald-500",
        gradient: "from-emerald-500 to-teal-500",
        bgLight: "bg-emerald-50",
        textColor: "text-emerald-600",
        minXp: 150,
        maxXp: 349,
        description: "Dominas las rectas, curvas, poligonales y paralelas como un auténtico cartógrafo."
    },
    {
        level: 3,
        title: "Arquitecto de Polígonos",
        icon: "📐",
        badgeColor: "bg-amber-500",
        gradient: "from-amber-500 to-orange-500",
        bgLight: "bg-amber-50",
        textColor: "text-amber-600",
        minXp: 350,
        maxXp: 599,
        description: "Conoces los lados, vértices y secretos de todas las figuras planas."
    },
    {
        level: 4,
        title: "Mago de la Simetría",
        icon: "🦋",
        badgeColor: "bg-purple-500",
        gradient: "from-purple-500 to-pink-500",
        bgLight: "bg-purple-50",
        textColor: "text-purple-600",
        minXp: 600,
        maxXp: 849,
        description: "Manejas el espejo mágico y los ejes de simetría con precisión asombrosa."
    },
    {
        level: 5,
        title: "Sabio del Espacio 3D",
        icon: "🪐",
        badgeColor: "bg-rose-500",
        gradient: "from-rose-500 to-indigo-600",
        bgLight: "bg-rose-50",
        textColor: "text-rose-600",
        minXp: 850,
        maxXp: 1000,
        description: "¡Has alcanzado la máxima maestría geométrica! Comprendes el espacio en tres dimensiones."
    }
];

/**
 * Calcula el XP total de un estudiante a partir de sus notas y progreso
 * @param {Object} studentProgress Objeto de progreso del alumno
 * @returns {number} Puntos totales de experiencia (XP)
 */
export const calculateStudentXP = (studentProgress) => {
    if (!studentProgress || typeof studentProgress !== 'object') return 0;

    let totalXP = 0;

    ['unit1', 'unit2', 'unit3', 'symmetry'].forEach(unitId => {
        const item = studentProgress[unitId];
        if (item) {
            if (item.unlocked) {
                totalXP += 50;
            }
            const score = typeof item.score === 'number' ? item.score : parseFloat(item.score) || 0;
            totalXP += Math.round(score * 20);
        }
    });

    return Math.min(Math.max(totalXP, 0), 1000);
};

/**
 * Devuelve la información completa del rango, nivel y progreso del estudiante
 * @param {number} xp Puntos de experiencia acumulados
 */
export const getStudentRank = (xp = 0) => {
    const safeXp = Math.max(0, Math.min(xp, 1000));

    let currentRank = RANKS[0];
    for (let i = RANKS.length - 1; i >= 0; i--) {
        if (safeXp >= RANKS[i].minXp) {
            currentRank = RANKS[i];
            break;
        }
    }

    const nextRank = RANKS.find(r => r.level === currentRank.level + 1) || null;
    const isMaxLevel = !nextRank;

    let progressPercent = 100;
    let xpRemaining = 0;

    if (!isMaxLevel && nextRank) {
        const xpInCurrentLevel = safeXp - currentRank.minXp;
        const totalXpInLevel = nextRank.minXp - currentRank.minXp;
        progressPercent = Math.min(Math.round((xpInCurrentLevel / totalXpInLevel) * 100), 100);
        xpRemaining = nextRank.minXp - safeXp;
    }

    return {
        ...currentRank,
        totalXp: safeXp,
        nextRank,
        isMaxLevel,
        progressPercent,
        xpRemaining
    };
};
